import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Plus, Book, Video, Broadcast, GraduationCap, Users, Sparkle, 
  Trash, Eye, Link, CreditCard, ArrowRight
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const funnelTypes = [
  { value: "ebook", label: "Ebook Funnel", icon: Book, description: "Free ebook download in exchange for email" },
  { value: "free_lesson", label: "Free Lesson Funnel", icon: Video, description: "Free lesson preview to drive signups" },
  { value: "webinar", label: "Webinar Funnel", icon: Broadcast, description: "Live or recorded webinar registration" },
  { value: "academy_signup", label: "Academy Signup Funnel", icon: GraduationCap, description: "Direct academy membership signup" },
  { value: "affiliate", label: "Affiliate Funnel", icon: Users, description: "Affiliate partner registration" },
];

const FunnelCard = ({ funnel, onDelete, onView }) => {
  const typeInfo = funnelTypes.find(t => t.value === funnel.funnel_type);
  const Icon = typeInfo?.icon || Book;
  const conversionRate = funnel.visitors > 0 
    ? ((funnel.conversions / funnel.visitors) * 100).toFixed(1) 
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 card-hover"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${funnel.is_active ? 'bg-emerald-500/20' : 'bg-zinc-700/20'}`}>
          <Icon size={24} className={funnel.is_active ? 'text-emerald-400' : 'text-zinc-500'} weight="duotone" />
        </div>
        <div className="flex items-center gap-2">
          <span className={`status-badge ${funnel.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
            {funnel.is_active ? 'Active' : 'Inactive'}
          </span>
          <button
            onClick={() => onDelete(funnel.id)}
            className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-rose-400 transition-colors"
          >
            <Trash size={16} />
          </button>
        </div>
      </div>

      <h3 className="text-lg font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {funnel.name}
      </h3>
      <p className="text-sm text-zinc-500 mb-4">{typeInfo?.label || funnel.funnel_type}</p>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Visitors</p>
          <p className="text-xl font-semibold text-white">{funnel.visitors}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Conversions</p>
          <p className="text-xl font-semibold text-emerald-400">{funnel.conversions}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Rate</p>
          <p className="text-xl font-semibold text-cyan-400">{conversionRate}%</p>
        </div>
      </div>

      <Button
        onClick={() => onView(funnel)}
        className="w-full h-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-sm"
      >
        <Eye size={16} className="mr-2" />
        View Funnel
      </Button>
    </motion.div>
  );
};

export default function FunnelBuilder() {
  const [funnels, setFunnels] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [funnelType, setFunnelType] = useState("ebook");
  const [description, setDescription] = useState("");

  useEffect(() => {
    fetchFunnels();
  }, []);

  const fetchFunnels = async () => {
    try {
      const res = await axios.get(`${API}/funnels`);
      setFunnels(res.data);
    } catch (err) {
      toast.error("Failed to fetch funnels");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!name) {
      toast.error("Please enter a funnel name");
      return;
    }

    try {
      const res = await axios.post(`${API}/funnels`, {
        name,
        funnel_type: funnelType,
        description,
      });
      setFunnels([res.data, ...funnels]);
      toast.success("Funnel created!");
      resetForm();
      setShowCreate(false);
    } catch (err) {
      toast.error("Failed to create funnel");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/funnels/${id}`);
      setFunnels(funnels.filter(f => f.id !== id));
      if (selectedFunnel?.id === id) setSelectedFunnel(null);
      toast.success("Funnel deleted");
    } catch (err) {
      toast.error("Failed to delete funnel");
    }
  };

  const handleGenerateContent = async () => {
    if (!selectedFunnel) return;
    setGenerating(true);
    
    try {
      const res = await axios.post(`${API}/funnels/${selectedFunnel.id}/generate-content`);
      toast.success("Funnel content generated!");
      console.log("Generated content:", res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate content");
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleActive = async (funnel) => {
    try {
      await axios.put(`${API}/funnels/${funnel.id}`, { is_active: !funnel.is_active });
      setFunnels(funnels.map(f => f.id === funnel.id ? { ...f, is_active: !f.is_active } : f));
      toast.success(funnel.is_active ? "Funnel deactivated" : "Funnel activated");
    } catch (err) {
      toast.error("Failed to update funnel");
    }
  };

  const resetForm = () => {
    setName("");
    setFunnelType("ebook");
    setDescription("");
  };

  return (
    <div data-testid="funnel-builder">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Funnel Builder
          </h1>
          <p className="text-zinc-400">Create marketing funnels to drive academy signups</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="create-funnel-btn"
          className="h-12 px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
        >
          <Plus size={20} className="mr-2" />
          New Funnel
        </Button>
      </div>

      {/* Funnel Type Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {funnelTypes.map(type => {
          const Icon = type.icon;
          const count = funnels.filter(f => f.funnel_type === type.value).length;
          return (
            <div
              key={type.value}
              className="bg-zinc-900/50 border border-white/5 rounded-2xl p-4 text-center"
            >
              <Icon size={28} className="mx-auto mb-2 text-amber-500" weight="duotone" />
              <p className="text-sm text-white font-medium">{type.label}</p>
              <p className="text-2xl font-bold text-white mt-1">{count}</p>
            </div>
          );
        })}
      </div>

      {/* Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Funnels List */}
        <div className="lg:col-span-2">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : funnels.length === 0 ? (
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-12 text-center">
              <GraduationCap size={48} className="mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-semibold text-white mb-2">No funnels yet</h3>
              <p className="text-zinc-400 mb-6">Create your first marketing funnel</p>
              <Button
                onClick={() => setShowCreate(true)}
                className="h-12 px-6 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              >
                Create Funnel
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {funnels.map((funnel) => (
                <FunnelCard
                  key={funnel.id}
                  funnel={funnel}
                  onDelete={handleDelete}
                  onView={setSelectedFunnel}
                />
              ))}
            </div>
          )}
        </div>

        {/* Funnel Details */}
        <div className="lg:col-span-1">
          {selectedFunnel ? (
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 sticky top-8">
              <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {selectedFunnel.name}
              </h3>

              {/* Funnel Steps */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center text-sm font-bold">1</div>
                  <span className="text-sm text-zinc-300">Landing Page</span>
                </div>
                <div className="ml-4 w-0.5 h-4 bg-zinc-700" />
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-cyan-500/20 text-cyan-500 flex items-center justify-center text-sm font-bold">2</div>
                  <span className="text-sm text-zinc-300">Lead Capture</span>
                </div>
                <div className="ml-4 w-0.5 h-4 bg-zinc-700" />
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-500 flex items-center justify-center text-sm font-bold">3</div>
                  <span className="text-sm text-zinc-300">Thank You Page</span>
                </div>
                <div className="ml-4 w-0.5 h-4 bg-zinc-700" />
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-violet-500/20 text-violet-500 flex items-center justify-center text-sm font-bold">4</div>
                  <span className="text-sm text-zinc-300">Email Sequence</span>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <Button
                  onClick={handleGenerateContent}
                  disabled={generating}
                  data-testid="generate-funnel-content-btn"
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-semibold"
                >
                  {generating ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Generating...
                    </span>
                  ) : (
                    <>
                      <Sparkle size={20} className="mr-2" weight="fill" />
                      Generate Content
                    </>
                  )}
                </Button>

                <Button
                  onClick={() => handleToggleActive(selectedFunnel)}
                  className={`w-full h-10 rounded-xl ${selectedFunnel.is_active ? 'bg-zinc-800 hover:bg-zinc-700 text-white' : 'bg-emerald-500 hover:bg-emerald-400 text-black'}`}
                >
                  {selectedFunnel.is_active ? 'Deactivate Funnel' : 'Activate Funnel'}
                </Button>

                <div className="grid grid-cols-2 gap-2">
                  <Button className="h-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-sm">
                    <Link size={16} className="mr-1" />
                    Copy Link
                  </Button>
                  <Button className="h-10 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white text-sm">
                    <CreditCard size={16} className="mr-1" />
                    Stripe
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-8 text-center">
              <Eye size={32} className="mx-auto mb-3 text-zinc-600" />
              <p className="text-zinc-400">Select a funnel to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Funnel Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Create New Funnel
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Funnel Name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Free Ebook Funnel"
                data-testid="funnel-name-input"
                className="h-12 bg-zinc-950 border-white/10 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Funnel Type</Label>
              <Select value={funnelType} onValueChange={setFunnelType}>
                <SelectTrigger className="h-12 bg-zinc-950 border-white/10 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {funnelTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <type.icon size={18} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">
                {funnelTypes.find(t => t.value === funnelType)?.description}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this funnel..."
                className="bg-zinc-950 border-white/10 rounded-xl min-h-[80px]"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setShowCreate(false)}
                className="flex-1 h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                data-testid="save-funnel-btn"
                className="flex-1 h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              >
                Create Funnel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
